import classNames from 'classnames';
import React, { FC, useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import { ExperimentNames } from '../../constants/ExperimentNames';
import { PageTabsNames } from '../../constants/PageTabsNames';
import { VideoMakerAscendSlug } from '../../constants/VideoMakerAscendSlug';
import { sentryClient } from '../../services/sentryClient';
import {
  generatePathnameForActiveTab,
  getActiveTabFromPathname,
} from '../../utils/activeTabPathname';
import { useExperiments } from '../../utils/hooks/useExperiments';
import { useWixmpSearchVideoMakerItems } from '../../utils/hooks/usePrivateWixmp/usePrivateWixmp';
import { HeaderActionsBar } from '../common/HeaderActionsBar';
import { NotificationContainer } from '../common/NotificationContainer';
import { PageTabs } from '../common/PageTabs';
import { Breadcrumbs, Page } from '../common/wixCommon';
import { FrameContent } from '../FrameContent';
import { MyVideosTab } from '../MyVideosTab';
import styles from './HomePage.scss';
import { IOuter } from './HomePage.types';

export const HomePage: FC<IOuter> = ({
  mediaToken,
  AscendUpgradeButtonComponent,
  AscendBadgeComponent,
}) => {
  const { formatMessage } = useIntl();
  const history = useHistory();

  const {
    isLoading,
    wixmpResponse,
    initRequest,
    requestError,
  } = useWixmpSearchVideoMakerItems({
    mediaToken,
  });

  useEffect(() => {
    initRequest().catch((error) => {
      throw error;
    });
  }, []);

  useEffect(() => {
    if (requestError) {
      sentryClient.captureException(requestError);
      return;
    }
  }, [requestError]);

  const [activeTab, setActiveTab] = useState(
    getActiveTabFromPathname(history.location.pathname),
  );

  useEffect(() => {
    setActiveTab(getActiveTabFromPathname(history.location.pathname));
  }, [history]);

  const handleChangeTabClick = useCallback(
    ({ id }: { id: PageTabsNames }) => {
      history.push({
        pathname: generatePathnameForActiveTab(id),
        search: history.location.search,
      });
      setActiveTab(id);
    },
    [setActiveTab, history],
  );

  const { isExperimentEnabled } = useExperiments();

  const isAscendAwarenessExperimentEnabled = isExperimentEnabled(
    ExperimentNames.AscendAwareness,
  );

  const pageHeaderTitle = formatMessage({
    id: 'video-maker-home.main.header.title',
  });

  return (
    <>
      <NotificationContainer />

      <Page maxWidth={969} minWidth={990} className={styles.pageHolder}>
        <Page.Header
          title={pageHeaderTitle}
          subtitle={formatMessage({
            id: 'video-maker-home.main.header.subtitle',
          })}
          breadcrumbs={(minimized) => (
            <Breadcrumbs
              activeId="1"
              items={[
                {
                  id: '1',
                  value: isAscendAwarenessExperimentEnabled ? (
                    <AscendBadgeComponent
                      slug={VideoMakerAscendSlug}
                      title={pageHeaderTitle}
                      minimized={minimized}
                    />
                  ) : minimized ? (
                    pageHeaderTitle
                  ) : (
                    <></>
                  ),
                },
              ]}
            />
          )}
          actionsBar={
            <div className={styles.actionsBarHolder}>
              {isAscendAwarenessExperimentEnabled ? (
                <AscendUpgradeButtonComponent
                  className={styles.ascendUpgradeButton}
                  slug={VideoMakerAscendSlug}
                  origin="BM_TopBar"
                  items={[
                    formatMessage({
                      id: 'video-maker-home.upgrade-ascend.bullet1',
                    }),
                    formatMessage({
                      id: 'video-maker-home.upgrade-ascend.bullet2',
                    }),
                    formatMessage({
                      id: 'video-maker-home.upgrade-ascend.bullet3',
                    }),
                  ]}
                />
              ) : null}
              <HeaderActionsBar activeTab={activeTab} />
            </div>
          }
        />

        <Page.Tail>
          <PageTabs activeTab={activeTab} onClick={handleChangeTabClick} />
        </Page.Tail>

        <Page.Content>
          <>
            <div
              className={classNames(styles.contentHolder, {
                [styles.hidden]: activeTab !== PageTabsNames.Templates,
              })}
            >
              <FrameContent />
            </div>

            {activeTab === PageTabsNames.MyVideos ? (
              <div className={classNames(styles.contentHolder)}>
                <MyVideosTab
                  mediaToken={mediaToken}
                  isLoading={isLoading}
                  files={(wixmpResponse && wixmpResponse.data) || []}
                  requestError={requestError}
                  onGetVideoMakerItems={initRequest}
                />
              </div>
            ) : null}
          </>
        </Page.Content>
      </Page>
    </>
  );
};
